import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Package, Search, ShieldCheck } from "lucide-react";
import { http, fmtErr } from "../lib/api";

export default function OrdersLookup() {
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null); // null = never searched
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await http.post("/orders/lookup", { email });
      setOrders(data.orders);
      if (data.orders.length === 0) toast("No paid orders found for that email");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Lookup failed");
    } finally { setLoading(false); }
  };

  const input = "w-full bg-white border border-[#E5D8C1] px-4 py-3 focus:outline-none focus:border-[#D32F2F]";

  return (
    <div className="bg-[#FFFDF8]" data-testid="orders-lookup-root">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <div className="text-xs tracking-[0.3em] uppercase text-[#D32F2F] mb-3">Order lookup</div>
        <h1 className="font-heading text-6xl uppercase tracking-wide">Find your order</h1>
        <p className="text-lg text-[#5C5A56] mt-4">Enter the email you used at checkout — we&apos;ll pull up your paid orders and their status. No account needed.</p>

        <form onSubmit={lookup} className="mt-10 bg-[#FDF4E3] border border-[#E5D8C1] p-8 flex flex-col md:flex-row gap-4">
          <input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className={input}
            data-testid="orders-lookup-email"
          />
          <button
            type="submit"
            disabled={loading}
            className="bg-[#D32F2F] text-white hover:bg-[#B71C1C] px-8 py-3 uppercase tracking-widest font-heading disabled:opacity-60 flex items-center justify-center gap-2 whitespace-nowrap"
            data-testid="orders-lookup-submit"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />} Find orders
          </button>
        </form>

        {orders !== null && (
          <div className="mt-10">
            {orders.length === 0 ? (
              <div className="border border-dashed border-[#E5D8C1] p-12 text-center text-[#5C5A56]" data-testid="orders-lookup-empty">
                <Package size={40} className="mx-auto mb-3 opacity-40" />
                No paid orders found for <b>{email}</b>. Double-check the email — or if you just paid, give the webhook a minute.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-xs uppercase tracking-widest text-[#5C5A56]">{orders.length} order{orders.length > 1 ? "s" : ""} found</div>
                {orders.map((o) => (
                  <div key={o.id} className="bg-white border border-[#E5D8C1] p-5" data-testid={`order-row-${o.id}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <div>
                        <div className="font-heading text-2xl">${(o.amount || 0).toFixed(2)}</div>
                        <div className="text-xs text-[#5C5A56] uppercase tracking-widest">{o.created_at?.slice(0, 10)} · {o.mode === "subscription" ? "Chamoy Club" : "Retail"} order</div>
                      </div>
                      <span className="bg-[#7CB342] text-white text-[10px] uppercase font-heading px-2 py-1 flex items-center gap-1"><ShieldCheck size={12} /> Paid</span>
                    </div>
                    {o.breakdown && o.breakdown.length > 0 && (
                      <div className="mt-4 border-t border-[#E5D8C1] pt-3 space-y-1 text-sm">
                        {o.breakdown.map((line, i) => (
                          <div key={i} className="flex justify-between">
                            <span>{line.bonus && "🎁 "}{line.name} × {line.quantity}</span>
                            <span className={line.bonus ? "text-[#7CB342] font-semibold" : "text-[#5C5A56]"}>{line.bonus ? "FREE" : `$${(line.line_total || 0).toFixed(2)}`}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {o.promo && o.promo.free_mix_jars > 0 && (
                      <div className="mt-3 text-xs text-[#7CB342]">🎁 {o.promo.free_mix_jars} bonus mixed chamoy jar{o.promo.free_mix_jars > 1 ? "s" : ""} included</div>
                    )}
                    {(o.tracking_number || o.fulfillment_status) && (
                      <div className="mt-4 border-t border-[#E5D8C1] pt-3 flex items-center gap-3" data-testid={`order-tracking-${o.id}`}>
                        <div className="bg-[#1A1A1A] text-[#FFB300] text-[10px] uppercase tracking-widest px-2 py-1 font-heading">{o.fulfillment_status || "pending"}</div>
                        {o.tracking_number && (
                          <div className="flex-1 text-sm">
                            <div className="text-xs uppercase tracking-widest text-[#5C5A56]">Tracking · {(o.carrier || "").toUpperCase()}</div>
                            <div className="font-mono">{o.tracking_number}</div>
                          </div>
                        )}
                        {o.tracking_url && (
                          <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-xs uppercase tracking-widest text-[#D32F2F] border-b border-[#D32F2F] pb-0.5">Track →</a>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-16 text-center text-sm text-[#5C5A56]">
          Wholesaler? <Link to="/portal" className="text-[#D32F2F] font-semibold">Sign in to your portal</Link> to see case orders.
        </div>
      </div>
    </div>
  );
}
